import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Text, type IconName } from '@/nova/components';

import {
  AddTile,
  Board,
  Count,
  Field,
  Head,
  Hint,
  Label,
  LabelRow,
  Open,
  Remove,
  Thumb,
  Thumbs,
} from './MoodBoardField.styles';

export interface MoodBoardFieldProps {
  /** The attached mood-board images (CDN urls) already saved on the draft. */
  images: string[];
  /** Images currently uploading to CDN (shown with a soft pulse until they land in `images`). */
  pending: { key: string; dataUri: string }[];
  /** The cap on total attachments (images + pending). */
  max: number;
  /** While true no new images can be added (a compose/generate is in flight). */
  disabled?: boolean;
  /** Add picked/dropped/pasted image files (the parent reads, uploads, then appends the urls). */
  onAddFiles: (files: File[]) => void;
  /** Remove the saved image at this index. */
  onRemove: (index: number) => void;
  /** Open the saved image at this index full size (the Art Style mood board). Left out, a thumb is only a picture;
   *  its remove button stays its own control either way. */
  onOpen?: (index: number) => void;
  /** The open button's accessible name (already translated); left out, it reads "Open image". */
  openLabel?: string;
  /** Copy overrides for a different home (the Art Style page's mood board and mascot fields). Each is an
   *  ALREADY-TRANSLATED string; left out, the storyboard's own copy shows. `emptyHint: null` hides that line. */
  label?: string;
  icon?: IconName;
  hint?: string;
  emptyHint?: string | null;
  removeLabel?: string;
  addTitle?: string;
  /** The add tile's own words (already translated); left out, it reads "Add". */
  addText?: string;
  /** Leave out the label and hints (the caller titles the board itself, e.g. a mascot card). */
  headless?: boolean;
  /** An "n/max" count in the board's lower-right corner (like the Art Style direction field's character count). */
  showCount?: boolean;
  /** The add tile in the primary (CTA orange) colour, for a board where adding is the owner's next step (the Art
   *  Style mood board, which an extraction needs). Left out, it stays the quiet dashed tile. */
  primaryAdd?: boolean;
}

/**
 * The mood-board attach control under the storyboard prompt: up to `max` reference images the owner drops,
 * picks or pastes to steer the WHOLE video's look and feel (they enhance, not replace, the promotion key art).
 * Page-local to the composer (not a shared component); mirrors the banner prompt composer's attach UX.
 */
export const MoodBoardField = ({
  images,
  pending,
  max,
  disabled = false,
  onAddFiles,
  onRemove,
  onOpen,
  openLabel,
  label,
  icon = 'palette',
  hint,
  emptyHint,
  removeLabel,
  addTitle,
  addText,
  headless = false,
  showCount = false,
  primaryAdd = false,
}: MoodBoardFieldProps): React.ReactElement => {
  const { t } = useTranslation();
  const [dragging, setDragging] = React.useState(false);
  // Room for another attachment (regardless of the disabled state), vs. whether one can actually be added
  // right now. The tile stays visible whenever there is room and only greys out while disabled, so a
  // compose/generate never makes the add affordance vanish (leaving an empty, broken-looking board).
  const underMax = images.length + pending.length < max;
  const hasImages = images.length + pending.length > 0;
  const canAdd = !disabled && underMax;

  const onDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    setDragging(false);
    if (!canAdd) return;
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
    if (files.length > 0) onAddFiles(files);
  };

  const onPaste = (e: React.ClipboardEvent): void => {
    if (!canAdd) return;
    const files = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith('image/'));
    if (files.length > 0) {
      e.preventDefault();
      onAddFiles(files);
    }
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length > 0) onAddFiles(files);
    e.target.value = ''; // let the same file be re-picked after a remove
  };

  return (
    <Field>
      {headless ? null : (
        <Head>
          <LabelRow>
            <Icon name={icon} size={15} />
            {label ? <Label raw text={label} tag="span" /> : <Label text="Mood board" tag="span" />}
          </LabelRow>
          {hint ? (
            <Hint raw text={hint} tag="span" />
          ) : (
            <Hint
              text="Up to {{n}} images that set the whole video's look and feel."
              placeholders={{ n: max }}
              tag="span"
            />
          )}
          {images.length === 0 && pending.length === 0 && emptyHint !== null ? (
            emptyHint ? (
              <Hint raw text={emptyHint} tag="span" />
            ) : (
              <Hint text="With none added, your promotions' key visuals drive the look and feel." tag="span" />
            )
          ) : null}
        </Head>
      )}
      <Board
        $dragging={dragging}
        onDragOver={(e) => {
          if (!canAdd) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onPaste={onPaste}
      >
        <Thumbs>
          {images.map((url, i) => (
            <Thumb key={url}>
              {onOpen ? (
                <Open type="button" aria-label={openLabel ?? t('Open image')} onClick={() => onOpen(i)}>
                  <img src={url} alt="" />
                </Open>
              ) : (
                <img src={url} alt="" />
              )}
              {!disabled ? (
                <Remove type="button" aria-label={removeLabel ?? t('Remove mood image')} onClick={() => onRemove(i)}>
                  <Icon name="close" size={11} />
                </Remove>
              ) : null}
            </Thumb>
          ))}
          {pending.map((p) => (
            <Thumb key={p.key} $pending>
              <img src={p.dataUri} alt="" />
            </Thumb>
          ))}
          {underMax ? (
            // Kept mounted while disabled (greyed, no file input) so the add affordance never disappears
            // mid-compose/generate. Hidden only when the attachment cap is reached. Its own words (addText) show only
            // while the board is empty; once images are in, it is a plain square "+" beside them.
            <AddTile
              $disabled={disabled}
              $wide={Boolean(addText) && !hasImages}
              $primary={primaryAdd}
              title={disabled ? t('Adding is paused while your ad is generating') : addTitle ?? t('Add mood image')}
            >
              <Icon name="plus" size={16} />
              {addText ? hasImages ? null : <Text raw text={addText} tag="span" /> : <Text text="Add" tag="span" />}
              {disabled ? null : (
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  aria-label={addTitle ?? t('Add mood image')}
                  onChange={onPick}
                />
              )}
            </AddTile>
          ) : null}
        </Thumbs>
        {showCount ? (
          <Count>
            <Text raw text={`${images.length + pending.length}/${max}`} tag="span" />
          </Count>
        ) : null}
      </Board>
    </Field>
  );
};

export default MoodBoardField;
